<template lang="pug">
.mmv(
  @fullscreenchange="onFullscreenChange($event)"
  @mousemove="showFullscreenBar()"
  @mouseover="showFullscreenBar()"
  @touchstart.passive="showFullscreenBar()"
  ref="root"
)
  .video-container(:class="fullscreen && 'is-fullscreen'")
    .pad
    .playing-pip(v-if="pictureInPicture")
      | {{__('This video is playing in Picture in Picture', eventNamespace)}}
    .videos(:class="split.length > 1 && 'multiple'" v-else)
      .video(v-for="stream in split" :key="stream.name")
        .toggle-audio(@click="setAudio(stream)" v-if="stream.hasAccess")
          Icon.audio-icon(name="media/splitvolenable")
        h4.message(v-if="stream.overlayMessage") {{__(stream.overlayMessage, 'auto') }}
        div(v-if="!stream.active || !stream.hasAccess")
          StaticImage(v-if="(stream.thumbnail || {}).src" alt="" :src="stream.thumbnail.src" :width="1600" :height="900" background="#000")
        YouTubeVideo(
          v-else-if="stream.type === 'YouTube'"
          :video-id="stream.videoId"
          :controls="controlsEligible ? stream.controls : null"
          :nativeControls="useNativeControls"
          :playerVars="ytPlayerVars"
          ref="yt"
          @unmute="setAudio(stream)"
          :key="stream.videoId"
          :autoplay="autoplay"
          :start="stream.start"
          :end="stream.end"
        )
        HlsVideo(
          v-else-if="stream.type === 'Cloudflare'"
          :stream="stream"
          :controls="controlsEligible ? stream.controls : null"
          :nativeControls="useNativeControls"
          :isLive="live"
          ref="hls"
          @airplay="canAirplay = $event"
          @unmute="setAudio(stream)"
          :key="stream.videoId || stream.videoSrcUrl"
          @fullscreenchange="fullscreen = $event"
        )
        YbvrVideo(
          v-else-if="stream.type === 'YBVR' && !$rogue.appContext"
          :contentURL="stream.videoSrcUrl"
          ref="ybvr"
          :stream="stream"
          :key="stream.videoId || stream.videoSrcUrl"
          :autoPlay="true"
        )
  //- The stream bar sits outside the fullscreen element, so mirror its controls in here
  .fullscreen-bar(
    v-if="fullscreen && useNativeControls"
    :class="{idle: fullscreenBarIdle}"
    @mouseenter="holdFullscreenBar()"
    @mouseleave="showFullscreenBar()"
    @pointerdown.capture="fullscreenBarPressedWhileIdle = fullscreenBarIdle"
    @click.capture="onFullscreenBarClick($event)"
  )
    button.fs-btn(
      v-if="supportSplitScreen"
      type="button"
      :class="{active: showSplit}"
      :aria-label="__('Stream Viewer', eventNamespace)"
      :aria-expanded="showSplit"
      @click="toggleSplitDrawer()"
    )
      Icon.fs-icn.mv(name="media/multiview" :fill="showSplit ? 'black' : 'white'")
    button.fs-btn(
      type="button"
      :aria-label="__('Exit Fullscreen', eventNamespace)"
      @click="toggleFullscreen()"
    )
      Icon.fs-icn(name="media/shrink" fill="white")
  .close-drawer(:class="{in: showSplit}" @click="showSplit = false")
  .split-drawer(:class="{in: showSplit}")
    h4.flex
      .close-btn(@click="showSplit = false")
        Icon.i(name="arrow-left")
      | {{__('Stream Viewer', eventNamespace)}}
    .stream-select
      Checkbox.cb(
        v-for="stream in splitStreams"
        :key="stream?.name"
        :modelValue="isSplit(stream.name)"
        @update:modelValue="toggleSplit(stream)"
        :label="stream.name"
      )
  client-only
    .controls(:class="{opaque: split.length === 1 && split[0].type === 'YouTube'}" v-if="showControls")
      .red-bar(v-if="live")
      .buttons
        .btn(@click="togglePlay()")
          Icon.icn(:name="split.length > 0 && split[0].controls.playing ? 'media/stopsmall' : 'media/playsmall'" fill="white")
        .btn(@click="next()" v-if="split.length === 1")
          Icon.icn(name="media/skipsmall" fill="white")
        .btn(@click="toggleMute()")
          Icon.icn(:name="muted ? 'media/mute' : 'media/volumeup'" fill="white")
        Icon.live-icn(name="media/live" fill="white" v-if="live")
        .fill
        .btn(@click="cast()" v-if="canAirplay")
          Icon.icn.mv(name="media/cast" fill="white")
        .btn.multiview(@click="showSplit = true" v-if="supportSplitScreen")
          Icon.icn.mv(name="media/multiview" fill="white")
        .btn(@click="toggleFullscreen()")
          Icon.icn(:name="fullscreen ? 'media/shrink' : 'media/fullscreen'" fill="white")
        .btn(@click="setPip()" v-if="split.length === 1")
          Icon.icn(name="media/pip" fill="white")
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, nextTick, type ComputedRef } from 'vue'
import { eventNamespace } from '../../js/constants'
import { useRogue } from '../../js/rogue'
import { useMQ } from '../../js/mq'
import type YbvrVideo from '../core/async/YbvrVideo.vue'
import type YouTubeVideo from '../core/async/YouTubeVideo.vue'
import type HlsVideo from './HlsVideo.vue'

const rogue = useRogue()
const mq = useMQ()

const pictureInPicture = inject<ComputedRef<any>>('pictureInPicture', computed(() => null))
const showPictureInPicture = inject('showPictureInPicture', () => {})
const overlay = inject<ComputedRef<string | null>>('overlay', () => computed(() => null), true)
const showOverlay = inject<(overlayName: string) => void>('showOverlay', (overlayName) => {})
const hideOverlay = inject<() => void>('hideOverlay', () => {})
const updateSelectedEvent = inject('updateSelectedEvent', () => { })

const props = defineProps({
  activeStream       : { type: Object, required: true },
  splitNames         : { type: Array, required: true },
  hasTicket          : { type: Boolean, default: false },
  supportSplitScreen : { type: Boolean, default: false },
  showCustomControls : { type: Boolean, default: true },
  ybvrSupported      : { type: Boolean, default: false },
  baseUrl            : { type: String, default: ''},
  autoplay           : { type: Boolean, default: false },
})

const emit = defineEmits(['setSplit'])

const playing = ref(false)
const volume = ref(0)
const showSplit = ref(false)
const fullscreen = ref(false)
const canAirplay = ref(false)

const root = ref<HTMLDivElement | null>(null)
const yt = ref<InstanceType<typeof YouTubeVideo>[] | null>(null)
const hls = ref<InstanceType<typeof HlsVideo>[] | null>(null)
const ybvr = ref<InstanceType<typeof YbvrVideo>[] | null>(null)

const live = computed(() => props.activeStream.live)
const streams = computed(() => props.activeStream.streamLinks || [])
const splitStreams = computed(() => streams.value.filter(s => s.type !== 'YBVR'))
const split = computed(() => (props.splitNames || []).filter(name => streams.value.find(s => s.name === name)).map(name => streams.value.find(s => s.name === name)))
const muted = computed(() => split.value.every(s => s.controls.volume === 0))
// Whether the players are driven by the shared controls state (play/volume sync across split streams)
const controlsEligible = computed(() => {
  if (mq.value === 'mobile' || mq.value === 'tablet') {
    return false
  }
  
  if (!live.value) {
    return false
  }

  if (split.value.every(s => s.type === 'YBVR')) {
    return false
  }

  // return split.value.every(s => s.type === 'YouTube')
  // iPhone has issues with fullscreen support. Using native controls instead.
  // const iPhone =
  //     (typeof window !== 'undefined' && (window as any).navigator.userAgent.toLowerCase().indexOf('iphone') > 0)
  //     || (rogue.isApp?.() && rogue.appContext.platform === 'ios')

  // return (props.supportSplitScreen || !iPhone) && split.value.every(s => s.type !== 'YBVR' && s.active && s.hasAccess)

  return split.value.every(s => s.type !== 'YBVR' && s.active && s.hasAccess)
})

// Native mode keeps the shared state but shows each player's own controls instead of our bar
const useNativeControls = computed(() => controlsEligible.value && !props.showCustomControls)
const showControls = computed(() => controlsEligible.value && props.showCustomControls)

// iPhone Safari can't fullscreen an element; native controls there fullscreen each video instead
const canFullscreen = typeof document !== 'undefined'
  && Boolean(document.fullscreenEnabled || (document as any).webkitFullscreenEnabled)

// Fullscreen toolbar fades out after the pointer goes idle and returns on movement.
// Mouse movement inside YouTube iframes never reaches us, so the faded bar stays hoverable as a reveal zone.
const FULLSCREEN_BAR_IDLE_MS = 3000
const fullscreenBarVisible = ref(true)
const fullscreenBarIdle = computed(() => !fullscreenBarVisible.value && !showSplit.value)
const fullscreenBarPressedWhileIdle = ref(false)
let fullscreenBarTimer: ReturnType<typeof setTimeout> | undefined

function holdFullscreenBar() {
  clearTimeout(fullscreenBarTimer)
  fullscreenBarVisible.value = true
}

function showFullscreenBar() {
  holdFullscreenBar()
  if (!fullscreen.value) {
    return
  }
  fullscreenBarTimer = setTimeout(() => {
    fullscreenBarVisible.value = false
  }, FULLSCREEN_BAR_IDLE_MS)
}

// A tap on the faded bar only reveals it, so nothing is triggered blind
function onFullscreenBarClick(e: MouseEvent) {
  if (fullscreenBarPressedWhileIdle.value) {
    e.stopPropagation()
    e.preventDefault()
  }
  fullscreenBarPressedWhileIdle.value = false
}

watch(fullscreen, (isFullscreen) => {
  if (isFullscreen) {
    showFullscreenBar()
  } else {
    clearTimeout(fullscreenBarTimer)
  }
})

// Play/pause/volume changes from native controls are activity we can see, even inside YouTube iframes
watch(() => split.value.map(s => `${s.controls?.playing}:${s.controls?.volume}`).join(), () => {
  if (fullscreen.value) {
    showFullscreenBar()
  }
})

onBeforeUnmount(() => clearTimeout(fullscreenBarTimer))

// YouTube's fullscreen nests inside ours and its exit button can't unwind it (only Esc can),
// so hide it, and the F shortcut that triggers it, wherever our fullscreen button is available
const ytPlayerVars = computed(() => ({
  playsinline: 1,
  ...(useNativeControls.value && canFullscreen ? { fs: 0, disablekb: 1 } : {}),
}))

function toggleSplitDrawer() {
  showSplit.value = !showSplit.value
}

// Split view is live-only; don't leave the drawer open on replays or tailgate shows
watch(controlsEligible, (eligible) => {
  if (!eligible) {
    showSplit.value = false
  }
})

// Check if the specified stream is currently in the split view
function isSplit(name: string) {
  return props.splitNames.includes(name)
}

// Turn a specific stream on or off in the split viewer
function toggleSplit(stream: any) {
  if (!props.hasTicket && stream.paid) {
    showOverlay('buyPremium')

    return
  }

  // Mute this stream before we add the video, otherwise Safari will stop the others
  if (props.splitNames.length > 0 && stream) {
    stream.controls.volume = 0
  }

  if (!props.splitNames.includes(stream.name)) {
    emit('setSplit', props.splitNames.concat(stream.name))
  } else if (props.splitNames.length > 1) {
    emit('setSplit', props.splitNames.filter(s => s !== stream.name))
  }

  hideOverlay()
  updatePlayStatus()
  if (typeof window !== 'undefined') {
    (window as any).mmvLog.push(`split: ${split.value.map(s => s.name).join(',')}`)
  }
}

function updatePlayStatus() {
  nextTick(() => {
    const hasVolume = split.value.find(s => s.controls.volume) || split.value[0]

    // Mute non-primary streams first
    streams.value.filter(s => s !== hasVolume).forEach(s => s.controls.volume = 0)
    // Mute non-primary streams first
    if (hasVolume) {
      hasVolume.controls.volume = 100
    }

    // Play them all
    streams.value.forEach(s => {
      s.controls.playing = split.value.some(s => s.controls.playing) && props.splitNames.includes(s.name)
    })
  })
}

// If a replay stream is playing, check if we should change the selected event
function updateStream() {
  if (split.value.length === 1 && props.activeStream.streamType === 'Replays' ) {
    const stream = split.value[0]
    let currentTime: number | null | undefined = null
    if (stream.type === 'YouTube') {
      currentTime = yt.value?.[0].getCurrentTime()
    } else if (stream.type === 'Cloudflare') {
      currentTime = hls.value?.[0].getCurrentTime()
    } else if (stream.type === 'YBVR' && ybvr.value) {
      currentTime = ybvr.value[0].getCurrentTime()
    }
    if (currentTime) {
      // This is breaking control room replays. It's updating the event while watching control room.
      // Seems like we need to go through and update the start times of the streams before we can enable this logic again.
      updateSelectedEvent(stream, currentTime)
    }
  }
}

function onFullscreenChange(e) {
  fullscreen.value = !!document?.fullscreenElement
}

function toggleFullscreen() {
  const el = root.value
  if (fullscreen.value) {
    fullscreen.value = false
    if (!document.fullscreenElement) {
      return
    }
    try {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) { /* IE11 */
        document.msExitFullscreen()
      }
    } catch (e) {
      console.log('Error existing fullscreen', e)
    }
  } else if (el) {
    fullscreen.value = true
    if (el.requestFullscreen) {
      el.requestFullscreen()
    } else if (el.webkitRequestFullscreen) { /* Safari */
      el.webkitRequestFullscreen()
    } else if (el.msRequestFullscreen) { /* IE11 */
      el.msRequestFullscreen()
    } else if (hls.value) {
      try {
        hls.value[0].videoEl?.webkitEnterFullscreen()
      } catch (e) {
        console.log('Error entering fullscreen', e)
      }
    }
  }
  if (typeof window !== 'undefined') {
    (window as any).mmvLog.push('toggle fullscreen')
  }
}

// Toggle all videos to play / pause at the same time
function togglePlay() {
  if (split.value.length) {
    const newVal = !split.value[0]?.controls.playing || false
    split.value.forEach(video => video.controls.playing = newVal)
  }
}

function toggleMute() {
  if (muted.value && split.value.length > 0) {
    split.value[0].controls.volume = 100
  } else {
    split.value.forEach(s => s.controls.volume = 0)
  }
}

function setAudio(stream) {
  split.value.forEach(s => s.controls.volume = s === stream ? 100 : 0)
}

// Swap to the next stream
function next() {
  if (!split.value.length) return

  const filteredStreams = streams.value
    .filter(s => s.type !== 'YBVR' || props.ybvrSupported)
    .filter(s => props.hasTicket || !s.paid)
  const index = (filteredStreams.indexOf(split.value[0]) + 1) % filteredStreams.length
  const newSplit = filteredStreams[index]
  newSplit.controls.playing = true
  emit('setSplit', [newSplit.name])
}

function cast() {
  let vid = hls.value || yt.value

  if (Array.isArray(vid)) {
    vid = vid[0]
  }

  if (!vid || !vid.cast) {
    return
  }

  vid.cast()
}

function setPip() {
  if (showPictureInPicture && split.value.length) {
    showPictureInPicture(split.value[0])
  }
  rogue?.router?.push(props.baseUrl)
}

function pauseAll() {
  if (split.value.length) {
    split.value.forEach(s => {
      s.controls.playing = false
    })
  }
  if (yt.value?.length) {
    yt.value.forEach(yt => {
      yt.pauseVideo()
    })
  }
  if (hls.value?.length) {
    hls.value.forEach(hls => {
      hls.play(false)
    })
  }
}

watch(overlay, (newVal) => {
  if (newVal === 'buyPremium' && fullscreen.value) {
    toggleFullscreen()
  } else if (newVal === 'ybvrNative') {
    pauseAll()
    hideOverlay()
  }
})

watch(() => props.hasTicket, (newVal, oldVal) => {
  if (!newVal && oldVal) {
    // if any videos are playing, but your ticket trial expired, stop them
    split.value
      .filter(s => s.paid)
      .forEach(s => {
        s.controls.playing = false
        showOverlay('buyPremium')
      })
  }
})

watch(() => props.hasTicket, (newValue, oldValue) => {
  const lostAccess = !newValue && oldValue
  if (lostAccess) {
    rogue.emitToApp?.('PremiumTrialEnded', {})
  }
})

if (typeof window !== 'undefined') {
  (window as any).mmvLog = (window as any).mmvLog || []
}

defineExpose({
  useNativeControls,
  toggleSplitDrawer,
  showSplit,
  toggleFullscreen,
  canFullscreen,
})

onMounted(() => {
  if (pictureInPicture.value && showPictureInPicture) {
    showPictureInPicture()
  }

  // const int = setInterval(() => {
  //   updateStream()
  // }, 5000)

  // onBeforeUnmount(() => {
  //   clearInterval(int)
  // })
})
</script>
<style lang="scss" scoped>
.mmv {
  position: relative;
  overflow: hidden;

  @include not-desktop() {
    min-width: 100vw;
  }
}
.red-bar {
  background-color: red;
  position: absolute;
  top: 0;
  height: $space-xxxs;
  left: $space-xxs;
  right: $space-xxs;
}

.controls {
  position: absolute;
  bottom: 0;
  height: 48px;
  width: 100%;
  z-index: 1;

  &.opaque {
    background-color: $color-black
  }
}

.fullscreen-bar {
  position: absolute;
  bottom: $space-s;
  right: $space-s;
  z-index: 1;
  display: flex;
  gap: $space-xs;
  transition: opacity 300ms ease;

  // Stays hoverable while faded so it can be revealed over YouTube tiles; keyboard focus keeps it visible
  &.idle:not(:focus-within) {
    opacity: 0;
  }
}

.fs-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 2px solid $color-white;
  border-radius: var(--btn-border-radius, 3px);
  background-color: rgba($color-black, 0.6);
  cursor: pointer;

  &:hover {
    background-color: $color-black;
  }

  &.active,
  &.active:hover {
    background-color: $color-white;
  }

  .fs-icn {
    width: $size-s;
    height: $size-s;

    &.mv {
      width: 24px;
    }
  }
}

.split-drawer {
  position: absolute;
  top: 0;
  z-index: 3;
  right: -288px;
  width: 288px;
  height: 100%;
  background-color: $color-white;
  transition: right linear 150ms;
  overflow-y: auto;

  &.in {
    right: 0;
  }

  .cb {
    margin-bottom: $space-s;
  }

  h4.flex {
    display: flex;
    align-items: center;
    background-color: $color-gray-x-light;
    padding: $space-s;
    margin: 0;
  }
  .stream-select {
    padding: $space-s;
  }
}

.close-btn {
  margin-right: $space-xs;
  cursor: pointer;

  .i {
    width: $size-xxs;
    height: $size-xxs;
  }
}

.close-drawer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.4);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;

  &.in {
    opacity: 1;
    pointer-events: all;
    z-index: 2;
  }
}

.toggle-audio {
  display: none;
  position: absolute;
  left: 50%;
  top: 50%;
  margin: -31px 0 0 -31px;
  z-index: 1;
  cursor: pointer;

  .audio-icon {
    width: 62px;
    height: 62px;
  }
}

.video-container {
  position: relative;
  width: 100%;
  > .pad {
    padding-top: 56.25%;
    background-color: black;
  }

  &.is-fullscreen {
    position: absolute;
    width: 100%;
    top: 50%;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
    margin: 0;
  }
}

.videos {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.video {
  flex-grow: 1;
  position: relative;

  .message {
    color: white;
    z-index: 2;
    background-color: black;
    padding: $space-s;
    position: absolute;
    margin-top: -$size-s;
    top: 50%;
    width: 100%;
    text-align: center;
  }
}

.videos.multiple {
   .video {
    padding: $space-xxs;
    width: 50%;
    flex-grow: 0;

    &:hover .toggle-audio {
      display: block
    }

    &:first-child:nth-last-child(n + 5), 
    &:first-child:nth-last-child(n + 5) ~ .video {
      /* This rule applies to the first child AND all subsequent siblings (~) 
         when the total count is 5 or more. */
      width: 33.333%; 
    }
  }
}

.buttons {
  display: flex;
  padding: 0 $space-s;

  .btn {
    cursor: pointer;
    height: $size-xxxxl;
    width: $size-xxxxl;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;

    &:hover {
      opacity: 1.0;
    }
  }

  .icn {
    height: $size-s;
    width: $size-s;

    &.mv {
      width: 24px;
    }
  }

  .live-icn {
    margin-left: $space-xs;
    width: $size-xxxl;
    height: $size-xxxxl;
  }

  .fill {
    flex-grow: 1;
  }
}

.selectable-videos {
  .video {
    cursor: pointer;
    overflow: hidden;
    position: relative;
    display: flex;
    margin: $size-s;
    width: 100%;
  }

  .live {
    text-transform: uppercase;
    font-size: 10px;
    font-weight: bold;
    position: absolute;
    bottom: $space-s;
    right: $space-s;
    padding: $space-xxxs $space-xxs;
    z-index: 1;
    background: red;
    color: white;
  }

  .thumb {
    position: relative;
    width: 160px;
    margin-right: $size-s;
  }

  .name {
    font-weight: bold;
    margin-bottom: $space-xxs;
  }

  .event-name {
    font-size: $size-xxs;
    margin-bottom: $space-xxs;
  }

  .paid {
    background: rgba(91, 189, 21, 0.2);;
    color: $color-green-dark;
    display: inline-block;
    padding: $space-xxxs $space-xxs;
    font-size: 10px;
    font-weight: bold;
  }
}
</style>
